// ── Offline / demo fallback content for the quest + market panels. ──
// When the api is unreachable, the in-game panels show this local set so the screens are
// never empty in single-player. The quest set mirrors the example in docs/features/quests.md
// and references real @mn/content species ids; the realms come from @mn/shared RealmId.

/** @typedef {import('@mn/shared').QuestDef} QuestDef */
/** @typedef {import('./api.js').DailyQuest} DailyQuest */

/**
 * A representative daily quest set (3 quests: easy / medium / hard), matching the
 * weighted-pool design in docs/features/quests.md. `progress` is omitted (read-only demo).
 * @type {DailyQuest[]}
 */
export const DEMO_DAILY_QUESTS = [
  {
    def: {
      id: 'q_visit_wilds',
      title: 'Scout the Wilds',
      description: 'Travel into the Wilds and look around.',
      realm: 'wilds',
      goal: { kind: 'visit', target: 'wilds', count: 1 },
      reward: { gold: 40, xp: 60 },
    },
  },
  {
    def: {
      id: 'q_cull_grublin',
      title: 'Cull the Swarm',
      description: 'Defeat 5 Grublin in the Wilds.',
      realm: 'wilds',
      goal: { kind: 'defeat', target: 'sp_grublin', count: 5 },
      reward: { gold: 90, xp: 150 },
    },
  },
  {
    def: {
      id: 'q_hunt_obsidrake',
      title: 'Apex Bounty: Obsidrake',
      description: 'Defeat the apex Obsidrake in the Badlands.',
      realm: 'badlands',
      goal: { kind: 'defeat', target: 'sp_obsidrake', count: 1 },
      reward: { gold: 180, xp: 300 },
    },
  },
];

/** @typedef {import('@mn/shared').MarketListing} MarketListing */

/**
 * A representative set of open market listings so the Marketplace board is never empty in
 * single-player / offline. Mix of items, gold and a monster, priced in $TOKEN.
 * @type {MarketListing[]}
 */
export const DEMO_MARKET_LISTINGS = [
  { id: 'm_potion', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'item', refId: 'it_potion', qty: 12, priceToken: 8, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_capball', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'item', refId: 'it_capture_ball', qty: 6, priceToken: 25, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_antidote', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'item', refId: 'it_antidote', qty: 9, priceToken: 14, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_revive', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'item', refId: 'it_revive', qty: 3, priceToken: 60, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_etheria', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'item', refId: 'it_ether', qty: 5, priceToken: 30, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_gold500', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'gold', refId: null, qty: 500, priceToken: 45, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
  { id: 'm_grublin', sellerId: 'demo', sellerWallet: 'DEMO', kind: 'monster', refId: 'sp_grublin', qty: 1, priceToken: 120, status: 'open', createdAt: '2026-01-01T00:00:00.000Z' },
];

/** Friendly display names for demo item refIds (and any matching api items). */
export const ITEM_DISPLAY_NAMES = Object.freeze({
  it_potion: 'Potion',
  it_capture_ball: 'Capture Ball',
  it_antidote: 'Antidote',
  it_revive: 'Revive',
  it_ether: 'Ether',
});
